import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p className="eyebrow mb-5">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] font-semibold text-zinc-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-zinc-500 mb-10">Last updated: 28 August 2026</p>

          <div className="max-w-none space-y-12 text-zinc-700 text-[15px] leading-7">
            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">1. Who we are</h2>
              <p>
                Club Pass is a service operated by Airsoft Ventures Limited ("Club Pass", "we", "us", "our"),
                a New Zealand company. This policy explains what personal information we collect through the
                Club Pass service, how we use it, and the choices you have.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">2. Information we collect</h2>
              <p className="mb-2">We collect the following categories of information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Account information:</strong> your name and email address, obtained via Google Sign-In when you create an account.</li>
                <li><strong>Organisation information:</strong> your club or organisation's name and contact email.</li>
                <li><strong>Member information:</strong> names, email addresses, membership IDs, expiry dates, and (optionally) photos of your club's members, which you or your club administrators upload in order to issue membership passes.</li>
                <li><strong>Billing information:</strong> subscription plan and status. Payment card details are collected and processed directly by Stripe, our payment processor — we never see or store your card number.</li>
                <li><strong>Usage information:</strong> basic technical logs (such as IP address and timestamps) generated automatically by our hosting and security providers for security and troubleshooting purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">3. How we use this information</h2>
              <p className="mb-2">We use the information above to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Create and issue digital membership passes to Google Wallet on your organisation's behalf.</li>
                <li>Send pass-related emails to your members (via our email delivery provider, Resend).</li>
                <li>Operate your account, including authentication and billing via Stripe.</li>
                <li>Maintain the security and reliability of the service.</li>
                <li>Communicate with you about your account or the service.</li>
              </ul>
              <p className="mt-2">We do not sell personal information, and we do not use member data for advertising.</p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">4. Who we share information with</h2>
              <p className="mb-2">
                We share information with the following third-party service providers, solely as needed to operate
                Club Pass:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Google</strong> — for account sign-in (Google OAuth) and to create and manage digital passes (Google Wallet API).</li>
                <li><strong>Stripe</strong> — for subscription billing and payment processing.</li>
                <li><strong>Resend</strong> — for delivering pass-related emails to your members.</li>
              </ul>
              <p className="mt-2">
                We do not share your information with any other third party except where required by law.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">5. Data retention</h2>
              <p>
                We retain your organisation's and members' information for as long as your account remains active,
                and for a reasonable period afterwards in case you wish to reactivate. You can request deletion of
                your organisation's data at any time by contacting us (see Section 8).
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">6. Your rights</h2>
              <p>
                Under the New Zealand Privacy Act 2020, you have the right to access and request correction of
                personal information we hold about you. If you are a club administrator, you are responsible for
                ensuring you have the appropriate authority from your members to upload their information to
                Club Pass, and for responding to your own members' requests regarding their data where appropriate.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">7. Security</h2>
              <p>
                We take reasonable technical and organisational measures to protect the information we hold,
                including encrypted connections (HTTPS) and access controls. No method of transmission or storage
                is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">8. Contact us</h2>
              <p>
                If you have questions about this policy or wish to exercise your privacy rights, contact us at{" "}
                <a href="mailto:info@airsoftsystems.co.nz" className="font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900">
                  info@airsoftsystems.co.nz
                </a>.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl font-semibold text-zinc-900 mb-3">9. Changes to this policy</h2>
              <p>
                We may update this policy from time to time. We will update the "Last updated" date above when we
                do, and material changes will be communicated to account owners directly.
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
